import React from 'react';
import styled from 'astroturf/react';

import { Input } from 'js/design_system/forms/Input';

import { DateFields } from './DateFields';
import { FormState } from './formState';
import { NameList } from './NameList';
import {
  CslFieldId,
  fieldLabel,
  isRequired,
  isVisible,
  SourceType,
} from './sourceTypes';

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  width: 100%;
`;

const Row = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  width: 100%;

  @include breakpoint(medium, large) {
    flex-direction: row;
  }

  & > * {
    flex: 1;
  }
`;

type SourceFormProps = {
  form: FormState;
  onChange: (form: FormState) => void;
};

function textInput(
  form: FormState,
  field: CslFieldId,
  onChange: (form: FormState) => void,
): React.ReactElement | null {
  if (!isVisible(form.type, field)) return null;
  const required = isRequired(form.type, field);
  const value = form[field as keyof FormState];
  if (typeof value !== 'string') return null;
  return (
    <Input
      key={field}
      label={`${fieldLabel(field, form.type)}${required ? ' *' : ''}`}
      value={value}
      autoComplete="off"
      onChange={(event) =>
        onChange({ ...form, [field]: event.target.value } as FormState)
      }
    />
  );
}

export function SourceForm({
  form,
  onChange,
}: SourceFormProps): React.ReactElement {
  const sourceType: SourceType = form.type;

  return (
    <Fields>
      {isVisible(sourceType, 'author') && (
        <NameList
          label="Author"
          organizationLabel="Organization as author"
          names={form.author}
          required={isRequired(sourceType, 'author')}
          onChange={(author) => onChange({ ...form, author })}
        />
      )}
      {isVisible(sourceType, 'editor') && (
        <NameList
          label="Editor"
          organizationLabel="Organization as editor"
          names={form.editor}
          required={isRequired(sourceType, 'editor')}
          onChange={(editor) => onChange({ ...form, editor })}
        />
      )}
      {textInput(form, 'title', onChange)}
      {textInput(form, 'container-title', onChange)}
      {(isVisible(sourceType, 'volume') ||
        isVisible(sourceType, 'issue') ||
        isVisible(sourceType, 'page')) && (
        <Row>
          {textInput(form, 'volume', onChange)}
          {textInput(form, 'issue', onChange)}
          {textInput(form, 'page', onChange)}
        </Row>
      )}
      {textInput(form, 'publisher', onChange)}
      {textInput(form, 'publisher-place', onChange)}
      {textInput(form, 'edition', onChange)}
      {isVisible(sourceType, 'issued') && (
        <DateFields
          label={fieldLabel('issued', sourceType)}
          required={isRequired(sourceType, 'issued')}
          value={form.issued}
          onChange={(issued) => onChange({ ...form, issued })}
        />
      )}
      {isVisible(sourceType, 'accessed') && (
        <DateFields
          label={fieldLabel('accessed', sourceType)}
          required={isRequired(sourceType, 'accessed')}
          value={form.accessed}
          onChange={(accessed) => onChange({ ...form, accessed })}
        />
      )}
      {textInput(form, 'URL', onChange)}
      {textInput(form, 'DOI', onChange)}
    </Fields>
  );
}
